import React, { useState, useMemo, useRef, useEffect } from "react";
import { X, ChevronDown, Check } from "lucide-react";
import "./FilterDrawer.css";

function MultiSelect({
  label,
  items,
  selectedItems,
  onToggle,
  placeholder,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return items;
    const query = searchQuery.toLowerCase().trim();
    return items.filter((item) => item.toLowerCase().includes(query));
  }, [items, searchQuery]);

  const handleContainerClick = () => {
    setIsOpen(true);
    inputRef.current?.focus();
  };

  const handleClearAll = (e) => {
    e.stopPropagation();
    selectedItems.forEach((item) => onToggle(item));
    setSearchQuery("");
  };

  const handleRemovePill = (e, item) => {
    e.stopPropagation();
    onToggle(item);
  };

  const handleSelect = (item) => {
    onToggle(item);
    setSearchQuery("");
    inputRef.current?.focus();
  };

  return (
    <div className="multi-select-section" ref={containerRef}>
      {/* Label */}
      <div className="multi-select-label">
        <span>{label}</span>
      </div>

      {/* Select box */}
      <div
        className={`multi-select-container ${isOpen ? "focused" : ""}`}
        onClick={handleContainerClick}
      >
        <div className="multi-select-values">
          {/* Selected pills */}
          {selectedItems.map((item) => (
            <span key={item} className="multi-select-pill">
              <span className="multi-select-pill-text">{item}</span>
              <button
                className="multi-select-pill-remove"
                onClick={(e) => handleRemovePill(e, item)}
                type="button"
              >
                <X size={12} />
              </button>
            </span>
          ))}

          {/* Inline search input */}
          <input
            ref={inputRef}
            type="text"
            className="multi-select-input"
            placeholder={selectedItems.length === 0 ? placeholder : ""}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setIsOpen(true)}
          />
        </div>

        {/* Action buttons */}
        <div className="multi-select-actions">
          {selectedItems.length > 0 && (
            <button
              className="multi-select-clear-btn"
              onClick={handleClearAll}
              type="button"
              title="Clear all"
            >
              <X size={14} />
            </button>
          )}
          <button
            className="multi-select-toggle-btn"
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen((prev) => !prev);
            }}
            type="button"
          >
            <ChevronDown
              size={16}
              className={`multi-select-chevron ${isOpen ? "rotated" : ""}`}
            />
          </button>
        </div>
      </div>

      {/* Dropdown list */}
      {isOpen && (
        <div className="multi-select-dropdown">
          {filteredItems.length === 0 ? (
            <div className="multi-select-no-results">No results found</div>
          ) : (
            filteredItems.map((item) => {
              const isSelected = selectedItems.includes(item);
              return (
                <div
                  key={item}
                  className={`multi-select-option ${isSelected ? "selected" : ""}`}
                  onClick={() => handleSelect(item)}
                >
                  <div className={`multi-select-checkbox ${isSelected ? "checked" : ""}`}>
                    {isSelected && <Check size={12} />}
                  </div>
                  <span className="multi-select-option-text">{item}</span>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

function FilterDrawer({
  isOpen,
  onClose,
  states,
  organisations,
  selectedStates,
  selectedOrganisations,
  appliedStates,
  appliedOrganisations,
  onToggleState,
  onToggleOrganisation,
  onApply,
  onClear,
  isLoading,
  isApplying,
  error,
  noResults,
}) {
  return (
    <>
      {/* Backdrop overlay */}
      <div
        className={`filter-drawer-overlay ${isOpen ? "open" : ""}`}
        onClick={onClose}
      />

      {/* Side panel */}
      <div className={`filter-drawer ${isOpen ? "open" : ""}`}>
        {/* Header */}
        <div className="filter-drawer-header">
          <h2>Filters</h2>
          <button className="filter-drawer-close-btn" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="filter-drawer-body">
          {noResults && (
            <div className="filter-error" style={{ marginBottom: "16px", backgroundColor: "#fef2f2", color: "#b91c1c", padding: "12px", borderRadius: "6px" }}>
              <p style={{ margin: 0 }}>No results found. Please adjust your filters.</p>
            </div>
          )}
          {isLoading ? (
            <div className="filter-loading">
              <div className="filter-shimmer" />
              <div className="filter-shimmer" />
              <div className="filter-shimmer short" />
            </div>
          ) : error ? (
            <div className="filter-error">
              <p>{error}</p>
            </div>
          ) : (
            <>
              <MultiSelect
                label="Organisations"
                items={organisations}
                selectedItems={selectedOrganisations}
                onToggle={onToggleOrganisation}
                placeholder="Search and select organisations..."
              />
              <MultiSelect
                label="States"
                items={states}
                selectedItems={selectedStates}
                onToggle={onToggleState}
                placeholder="Search and select states..."
              />
            </>
          )}
        </div>

        {/* Footer with Apply and Clear buttons */}
        <div className="filter-drawer-footer">
          <button
            className="filter-btn filter-btn-clear"
            onClick={onClear}
            disabled={isApplying}
          >
            Clear
          </button>
          <button
            className="filter-btn filter-btn-apply"
            onClick={onApply}
            disabled={isLoading || isApplying}
          >
            {isApplying ? (
              <span className="filter-btn-loading">
                <span className="filter-spinner" />
                Applying
              </span>
            ) : (
              "Apply"
            )}
          </button>
        </div>
      </div>
    </>
  );
}

export default FilterDrawer;
